import React, { useEffect, useMemo, useState } from 'react';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import { Input, Select, Button, DangerButton, SecondaryButton } from '../styles/TableStyles';
import { savedToast, errorToast, deletedToast } from '../utils/toastUtils';

const initialForm = { CommodityID: '', Variety: '' };

const VarietiesPage = () => {
  const [rows, setRows] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const collator = useMemo(() => new Intl.Collator(undefined, { sensitivity: 'base' }), []);

  const fetchRows = async () => {
    try {
      const res = await authAxios.get('/api/varieties');
      setRows(res.data);
    } catch (e) { console.error('Fetch varieties failed', e); }
  };

  const fetchCommodities = async () => {
    try {
      // Need UniqueID for the select value; use full list endpoint
      const res = await authAxios.get('/api/commodities');
      setCommodities(res.data);
    } catch (e) { console.error('Fetch commodities failed', e); }
  };

  useEffect(() => { fetchRows(); fetchCommodities(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.CommodityID) return alert('Commodity is required');
    const parsedCommodityId = Number(formData.CommodityID);
    if (!Number.isInteger(parsedCommodityId) || parsedCommodityId <= 0) {
      return alert('Please select a valid commodity');
    }
    if (!formData.Variety?.trim()) return alert('Variety is required');
    try {
      setSubmitting(true);
      if (isEditing) {
        await authAxios.put(`/api/varieties/${editingId}`, {
          CommodityID: parsedCommodityId,
          Variety: formData.Variety,
        });
      } else {
        await authAxios.post('/api/varieties', {
          CommodityID: parsedCommodityId,
          Variety: formData.Variety,
        });
      }
      setFormData(initialForm);
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      fetchRows();
      savedToast('Variety');
    } catch (e) {
      console.error('Submit failed', e);
      errorToast();
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (row) => {
    setFormData({ CommodityID: String(row.CommodityID ?? ''), Variety: row.Variety ?? '' });
    setEditingId(row.UniqueID);
    setIsEditing(true);
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this variety?')) return;
    try {
      await authAxios.delete(`/api/varieties/${id}`);
      fetchRows();
      deletedToast('Variety');
    } catch (e) { console.error('Delete failed', e); }
  };

  const columns = [
    { key: 'CommodityName', label: 'Commodity' },
    { key: 'Variety', label: 'Variety' },
  ];

  const sorted = useMemo(() => (
    [...rows].sort((a, b) => {
      const aKey = `${a.CommodityName ?? ''} ${a.Variety ?? ''}`;
      const bKey = `${b.CommodityName ?? ''} ${b.Variety ?? ''}`;
      return collator.compare(aKey, bKey);
    })
  ), [rows, collator]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return sorted.filter(r =>
      (r.CommodityName || '').toLowerCase().includes(q) ||
      (r.Variety || '').toLowerCase().includes(q)
    );
  }, [sorted, searchQuery]);

  return (
    <StandardTableLayout
      title="Varieties"
      showForm={showForm}
      submitting={submitting}
      onToggleForm={() => { setShowForm(!showForm); setFormData(initialForm); setIsEditing(false); setEditingId(null); }}
      onSubmit={handleSubmit}
      columns={columns}
      formData={formData}
      handleChange={handleChange}
      rows={filtered}
      editingId={editingId}
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      renderCell={(col, data, handleChange) => {
        // Non-edit mode: render plain text
        if (!handleChange) {
          return (
            <span style={{ background: 'transparent', display: 'inline-block', padding: '4px 0' }}>
              {String(data?.[col] ?? '')}
            </span>
          );
        }
        // Edit mode
        if (col === 'CommodityName') {
          return (
            <Select name="CommodityID" value={data.CommodityID} onChange={handleChange}>
              <option value="">Select commodity</option>
              {commodities.map(c => (
                <option key={c.UniqueID ?? c.Commodity} value={c.UniqueID}>{c.Commodity}</option>
              ))}
            </Select>
          );
        }
        if (col === 'Variety') {
          return (
            <Input type="text" name="Variety" value={data.Variety} onChange={handleChange} />
          );
        }
        return null;
      }}
      renderActions={(row) => (
        editingId === row.UniqueID ? (
          <>
            <Button type="submit" disabled={submitting}>Save</Button>
            <SecondaryButton type="button" onClick={() => { setIsEditing(false); setEditingId(null); setFormData(initialForm); }}>Cancel</SecondaryButton>
          </>
        ) : (
          <>
            <SecondaryButton type="button" onClick={() => handleEdit(row)}>Edit</SecondaryButton>
            <DangerButton type="button" onClick={() => handleDelete(row.UniqueID)}>Delete</DangerButton>
          </>
        )
      )}
    />
  );
};

export default VarietiesPage;


